import { DialogContent, DialogTitle, DialogDescription, Dialog } from "@/components/ui/dialog"
import { SubscriptionButtons } from "@/components/settings/subscription/SubscriptionButtons"
import { useOnMessage } from "./window.open.hook"
import { ResourceUsage, QuotaResource } from "@/types/subscription"
import React, { useState } from "react"
import { useParams } from "next/navigation"
import { useTeam } from "@/api/teams"

const RESOURCE_MESSAGES: Partial<Record<QuotaResource, React.ReactNode>> & {
  default: React.ReactNode
} = {
  seat: (
    <>
      <DialogTitle>Seat Limit Reached</DialogTitle>
      <DialogDescription>
        You've reached your maximum number of team members. Purchase more seats or remove existing
        team members to send more invites.
      </DialogDescription>
    </>
  ),
  chat: (
    <>
      <DialogTitle>Chat Limit Reached</DialogTitle>
      <DialogDescription>
        You've hit the limit for chats on your current plan. Upgrade your subscription to get more
        chat conversations.
      </DialogDescription>
    </>
  ),
  task: (
    <>
      <DialogTitle>Task Limit Reached</DialogTitle>
      <DialogDescription>
        You've reached the maximum number of Genie tasks permitted for your current plan. Upgrade
        your subscription to create more tasks.
      </DialogDescription>
    </>
  ),
  project: (
    <>
      <DialogTitle>Project Limit Reached</DialogTitle>
      <DialogDescription>
        You've reached the maximum number of projects permitted for your current plan. Upgrade your
        subscription to create more projects, or remove an existing project.
      </DialogDescription>
    </>
  ),
  default: (
    <>
      <DialogTitle>Quota Limit Reached</DialogTitle>
      <DialogDescription>
        You've reached the limit on your current plan. Upgrade your subscription or purchase more
        seats to increase your limits.
      </DialogDescription>
    </>
  ),
}

export const UpgradeSubscription = () => {
  const [showBilling, setShowBilling] = useState(false)
  const [resourceUsage, setResourceUsage] = useState<ResourceUsage | null>(null)
  const [messageOverride, setMessageOverride] = useState<{
    title?: string
    description?: string
  } | null>(null)

  const { team: teamSlug } = useParams<{ team: string }>()
  const team = useTeam(teamSlug)

  useOnMessage(
    "QUOTA_EXCEEDED",
    (
      event: MessageEvent<{
        quota: ResourceUsage
        teamSlug: string
        metadata?: { title?: string; description?: string }
      }>,
    ) => {
      setShowBilling(true)
      setResourceUsage(event.data.quota)

      if (event.data.metadata) {
        setMessageOverride(event.data.metadata)
      }
    },
  )

  if (!team.isSuccess || !resourceUsage) {
    return
  }

  const quotaMessage = RESOURCE_MESSAGES[resourceUsage.resource] || RESOURCE_MESSAGES.default

  return (
    <Dialog
      open={showBilling}
      onOpenChange={(open) => {
        setShowBilling(open)
        if (!open) {
          setResourceUsage(null)
          setMessageOverride(null)
        }
      }}
    >
      <DialogContent className="max-w-xl">
        {messageOverride ? (
          <>
            {messageOverride.title && <DialogTitle>{messageOverride.title}</DialogTitle>}
            {messageOverride.description && (
              <DialogDescription>{messageOverride.description}</DialogDescription>
            )}
          </>
        ) : (
          quotaMessage
        )}

        <SubscriptionButtons team={team.data} resourceUsage={[resourceUsage]} />
      </DialogContent>
    </Dialog>
  )
}
